import { useEffect, useMemo, useState, type ReactNode } from "react";

import type { LiveView } from "../../game/types";
import { useT } from "../../i18n";
import { useDialogoDeAtalhos } from "../../ui/useAtalhos";
import { Partida, type Situacao } from "./Partida";
import type { TrocaDaPartida } from "./SubstituicaoNoCampo";
import { especieDoLance, jogadaDe, type LadoDaPartida, type PartidaView } from "./tipos";

/**
 * A partida do modo solo, na página inteira.
 *
 * O motor roda no aparelho e esta tela pede o próximo trecho quando quer: a
 * cada passo chegam os lances de cinco minutos, e o campo os desenha no tempo
 * do ritmo escolhido. A partida para no intervalo, sozinha, e quando o
 * treinador abre a substituição: é a única pausa, e nela dá para mexer no
 * time. O "pular para o resultado" fecha a rodada de onde ela estiver.
 *
 * Assistir não muda nada. O motor simula a mesma partida, nos mesmos pedaços,
 * e há teste garantindo que a rodada assistida devolve um mundo idêntico ao da
 * rodada pulada.
 */

/** A velocidade da física em cada ritmo: segundos de jogo por segundo real. */
const RITMOS = [1, 2, 4, 10, 20] as const;

const ROTULO_DO_RITMO = [
  "aoVivo.ritmo1x",
  "aoVivo.ritmo2x",
  "aoVivo.ritmo4x",
  "aoVivo.ritmo10x",
  "aoVivo.ritmo20x",
] as const;

export function partidaDoLive(live: LiveView): PartidaView {
  const lado = (qual: "home" | "away"): LadoDaPartida => {
    const l = live.lados[qual];
    return {
      clubId: l.clubId,
      name: qual === "home" ? live.homeName : live.awayName,
      short: qual === "home" ? live.homeShort : live.awayShort,
      colors: qual === "home" ? live.homeColors : live.awayColors,
      formation: l.formation,
      slots: l.slots,
      custom: l.custom,
      emCampo: l.emCampo.map((vaga) => ({ jogador: vaga.jogador, sentOff: vaga.sentOff })),
    };
  };
  return {
    competition: live.competition,
    stage: live.stage,
    home: lado("home"),
    away: lado("away"),
    homeGoals: live.homeGoals,
    awayGoals: live.awayGoals,
    minute: live.minute,
    finished: live.finished || live.segment >= live.totalSegments,
    myClubId: live.myClubId,
    lances: live.events.map((evento, ordem) => ({
      ordem,
      minute: evento.minute,
      kind: especieDoLance(evento.kind, evento.key),
      clubId: evento.clubId,
      playerId: evento.playerId,
      assistId: evento.assistId,
      shooterId: evento.shooterId,
      play: jogadaDe(evento.play),
      key: evento.key,
      params: evento.params,
    })),
    placares: live.placares,
  };
}

/** Onde a velocidade escolhida fica guardada, para a próxima partida abrir nela. */
const CHAVE_DO_RITMO = "lm:partida:ritmo";

function ritmoGuardado(): number {
  try {
    const bruto = Number(localStorage.getItem(CHAVE_DO_RITMO));
    return Number.isInteger(bruto) && bruto >= 0 && bruto < RITMOS.length ? bruto : 0;
  } catch {
    return 0;
  }
}

export function PartidaSolo({
  live,
  busy,
  onStep,
  onSub,
  onShape,
  onFinish,
}: {
  live: LiveView;
  busy: boolean;
  onStep: (untilSegment: number) => void;
  onSub: (outId: string, inId: string) => void;
  /** O ajuste tático do intervalo: formação e a ordem das vagas. */
  onShape?: (formation: string, starters: string[]) => void;
  onFinish: () => void;
}): ReactNode {
  const t = useT();
  const [ritmo, setRitmo] = useState(ritmoGuardado);
  useEffect(() => {
    try {
      localStorage.setItem(CHAVE_DO_RITMO, String(ritmo));
    } catch {
      // Sem armazenamento, a velocidade vale só nesta partida.
    }
  }, [ritmo]);
  const [trocando, setTrocando] = useState(false);
  const [desenhando, setDesenhando] = useState(false);

  const partida = useMemo(() => partidaDoLive(live), [live]);
  // O jogo só acaba na tela quando o campo terminou de desenhar o último trecho.
  const acabou = partida.finished && !desenhando;
  /*
   * O intervalo é a pausa do motor; a substituição é a do treinador. O motor
   * chega ao intervalo quando entrega o último trecho do
   * primeiro tempo, mas o campo ainda está desenhando esse trecho: o intervalo
   * só aparece quando o desenho termina, senão a tela diz 45' com a bola rolando.
   */
  const emPausa = live.atPause && !acabou;
  const intervalo = emPausa && !desenhando;
  useEffect(() => {
    if (intervalo) setTrocando(true);
  }, [intervalo]);

  /*
   * O próximo trecho só é pedido quando o campo terminou de desenhar o
   * anterior. Cada trecho dura o que a jogada dele precisa, na velocidade do
   * ritmo escolhido; pedir de tanto em tanto tempo obrigava a física a
   * correr ou arrastar para caber no relógio. O primeiro vem na hora: a bola
   * rola assim que a página abre.
   */
  useEffect(() => {
    if (acabou || emPausa || trocando || busy || desenhando) return;
    const id = window.setTimeout(() => {
      // Já conta como desenhando: entre o pedido e o primeiro quadro do
      // trecho, a tela não pode achar que o jogo parou.
      setDesenhando(true);
      onStep(live.segment + 1);
    }, live.segment === 0 ? 300 : 250);
    return () => window.clearTimeout(id);
  }, [acabou, emPausa, trocando, busy, desenhando, live.segment, onStep]);

  function voltarAoJogo(): void {
    setTrocando(false);
    if (emPausa) {
      setDesenhando(true);
      onStep(live.segment + 1);
    }
  }

  useDialogoDeAtalhos({
    nome: "Partida",
    nivel: 28,
    // O Esc fecha a substituição, fora do intervalo; no fim, abre o resumo.
    aoFechar: () => {
      if (busy) return;
      if (acabou) onFinish();
      else if (trocando && !intervalo) voltarAoJogo();
    },
    atalhos: {
      espaco: () => {
        if (acabou) return;
        if (trocando) voltarAoJogo();
        else setTrocando(true);
      },
      "1": () => setRitmo(0),
      "2": () => setRitmo(1),
      "3": () => setRitmo(2),
      "4": () => setRitmo(3),
      "5": () => setRitmo(4),
      enter: () => {
        if (busy) return;
        if (trocando) {
          voltarAoJogo();
          return;
        }
        if (acabou) onFinish();
      },
    },
  });

  const situacao: Situacao = acabou ? "fim" : intervalo ? "intervalo" : trocando ? "pausado" : "jogando";
  const meuLado = live.iAmHome ? partida.home : partida.away;

  const troca: TrocaDaPartida = {
    lado: meuLado,
    emCampo: live.onPitch,
    banco: live.bench,
    trocasRestantes: live.subsLeft,
    bloqueado: acabou,
    aoTrocar: onSub,
    // Só no intervalo o treinador redesenha o time: formação e quem joga onde.
    ...(intervalo && onShape ? { ajuste: { aoAjustar: onShape } } : {}),
    // No intervalo não há para onde voltar: o jogo só segue pelo "Continuar".
    acoes: (
      <>
        {!intervalo && (
          <button type="button" className="ghost" onClick={() => setTrocando(false)}>
            {t("partida.voltar")}
          </button>
        )}
        <button type="button" className="primary" disabled={busy} onClick={voltarAoJogo}>
          {t("partida.continuarOJogo")}
        </button>
      </>
    ),
  };

  // O ritmo, a substituição, que é a pausa, e o pular. Com a substituição
  // aberta, quem continua o jogo é o cartão dela e o alto fica só com o
  // pular. No fim, o alto fica vazio: o "Ver o resumo" é o do meio do campo.
  const controles = (
    <>
      {!acabou && !trocando && (
        <>
          <div className="pf-ritmos">
            {RITMOS.map((_, indice) => (
              <button
                key={indice}
                type="button"
                aria-pressed={ritmo === indice}
                onClick={() => setRitmo(indice)}
              >
                {t(ROTULO_DO_RITMO[indice] ?? "aoVivo.ritmo1x")}
              </button>
            ))}
          </div>
          <button type="button" className="ghost" disabled={busy} onClick={() => setTrocando(true)}>
            {t("partida.substituicao")}
          </button>
        </>
      )}
      {!acabou && (
        <button type="button" className="ghost" disabled={busy} onClick={onFinish}>
          {t("aoVivo.pularParaResultado")}
        </button>
      )}
    </>
  );

  return (
    <Partida
      partida={partida}
      velocidade={RITMOS[ritmo] ?? 1}
      parado={emPausa || trocando || acabou}
      situacao={situacao}
      controles={controles}
      troca={troca}
      trocaAberta={trocando && !acabou}
      aoDesenhar={setDesenhando}
      acaoDoFim={
        <button type="button" className="primary" disabled={busy} onClick={onFinish}>
          {t("aoVivo.verResumo")}
        </button>
      }
    />
  );
}
